import { MinusIcon, TrendingDownIcon, TrendingUpIcon } from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState } from 'react';

import { cn } from '../cn';
import { Panel } from './primitives';

/**
 * Reporting pieces: the headline stat tile and the bar trend chart. Plain
 * SVG on purpose — one series over time needs no charting library, and every
 * portal app that shows a number over time should look the same.
 */

// ─── Stat tile ──────────────────────────────────────────────────────────────

export interface StatTileProps {
  readonly label: string;
  readonly value: string;
  /** Rendered next to the label — e.g. a hover info icon explaining the metric. */
  readonly labelAccessory?: React.ReactNode;
  /** Muted line under the value — the qualifier ("vs 212 last month"). */
  readonly hint?: string;
  /**
   * A signed change to show beside the hint. Positive reads as good unless
   * `downIsGood`. `null` renders nothing (no baseline to compare with).
   */
  readonly changePct?: number | null;
  /** What follows the change figure: "%" by default, or e.g. " places" for a search position. */
  readonly changeSuffix?: string;
  readonly downIsGood?: boolean;
  /**
   * Makes the tile a button, e.g. to show its metric on the chart below.
   * With it, `selected` marks the tile that's showing.
   */
  readonly onSelect?: () => void;
  readonly selected?: boolean;
  readonly className?: string;
}

export interface ChangeBadgeProps {
  /** The signed change. */
  readonly value: number;
  /** After the figure: "%" by default; "" for a plain number such as places moved. */
  readonly suffix?: string;
  /** A fall reads as good (e.g. cost). */
  readonly downIsGood?: boolean;
  /** Hover text, e.g. the earlier value. */
  readonly title?: string;
  readonly className?: string;
}

/**
 * A change with its direction: an arrow and the size, green when it's good,
 * red when it's bad, grey when flat.
 *
 * @param props - The change and how to read it.
 * @returns The badge.
 */
export function ChangeBadge({ value, suffix = '%', downIsGood = false, title, className }: ChangeBadgeProps): React.ReactElement {
  const improving = downIsGood ? value < 0 : value > 0;
  const size = Math.abs(value);
  return (
    <span
      title={title}
      className={cn(
        'font-bold whitespace-nowrap tabular-nums',
        value === 0 ? 'text-portal-muted' : improving ? 'text-portal-success' : 'text-rose-700',
        className,
      )}
    >
      {value > 0 ? (
        <TrendingUpIcon aria-hidden className="inline size-3.5 shrink-0" />
      ) : value < 0 ? (
        <TrendingDownIcon aria-hidden className="inline size-3.5 shrink-0" />
      ) : (
        <MinusIcon aria-hidden className="inline size-3.5 shrink-0" />
      )}{' '}
      {/* The arrow and the color carry the direction on screen; this carries it to a screen reader. */}
      <span className="sr-only">{value > 0 ? 'up ' : value < 0 ? 'down ' : 'no change, '}</span>
      {size.toFixed(size % 1 === 0 ? 0 : 1)}
      {suffix}
    </span>
  );
}

/**
 * One headline figure — the "how many" a report opens with.
 *
 * @param props - Label, value, and the optional trend/qualifier.
 * @returns The tile.
 */
export function StatTile({
  label,
  value,
  labelAccessory,
  hint,
  changePct = null,
  changeSuffix = '%',
  downIsGood = false,
  onSelect,
  selected = false,
  className,
}: StatTileProps): React.ReactElement {
  const body = (
    <>
      <p className="flex items-center gap-1 text-portal-micro font-bold tracking-[0.6px] text-portal-muted uppercase">
        {label}
        {labelAccessory}
      </p>
      <p className="mt-2 text-[22px] leading-none font-bold text-portal-ink tabular-nums sm:text-[28px]">{value}</p>
      {hint !== undefined || changePct !== null ? (
        <p className="mt-1.5 flex flex-wrap items-baseline gap-x-2 text-portal-micro text-portal-muted">
          {changePct !== null ? <ChangeBadge value={changePct} suffix={changeSuffix} downIsGood={downIsGood} /> : null}
          {hint !== undefined ? <span>{hint}</span> : null}
        </p>
      ) : null}
    </>
  );
  if (!onSelect) {
    // Tighter on phones, where tiles sit two to a row.
    return <Panel className={cn('p-4 sm:p-5', className)}>{body}</Panel>;
  }
  return (
    <Panel
      className={cn(
        'relative transition-colors',
        selected ? 'border-brand shadow-[0_0_0_1px_var(--color-brand)]' : 'hover:border-portal-ink',
        className,
      )}
    >
      {/* The whole tile picks the metric; the info icon stays its own control above it. */}
      <button
        type="button"
        aria-pressed={selected}
        aria-label={`Show ${label} on the chart`}
        onClick={onSelect}
        className="absolute inset-0 cursor-pointer rounded-[inherit] focus-visible:outline-2 focus-visible:outline-brand"
      />
      <div className="pointer-events-none relative p-4 sm:p-5 [&_button]:pointer-events-auto">{body}</div>
    </Panel>
  );
}

// ─── Progress bar ────────────────────────────────────────────────────────────

export interface ProgressBarProps {
  /** Names the bar for assistive tech ("Progress to planned sessions"). */
  readonly label: string;
  readonly value: number;
  readonly max: number;
  /** Left of the bar's caption, e.g. "1,690 of 5,480 paid sessions". */
  readonly caption?: React.ReactNode;
  /** Right of the caption in place of the percent, e.g. "$262 per lead" when the bar compares amounts. */
  readonly valueLabel?: React.ReactNode;
  readonly className?: string;
}

/**
 * How far along something is: a caption with the percent (or `valueLabel`)
 * at the right over a brand-colored bar. The fill stops at the end when
 * `value` passes `max`.
 *
 * @param props - The value, its maximum and the caption.
 * @returns The bar.
 */
export function ProgressBar({ label, value, max, caption, valueLabel, className }: ProgressBarProps): React.ReactElement {
  const share = max > 0 ? Math.min(1, Math.max(0, value / max)) : 0;
  const percent = Math.round(share * 100);
  return (
    <div className={className}>
      <div className="flex items-baseline justify-between gap-3 text-portal-micro">
        <span className="font-bold">{caption}</span>
        <span className="text-portal-muted">{valueLabel ?? `${percent}%`}</span>
      </div>
      <div
        role="progressbar"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={max}
        aria-valuenow={Math.min(value, max)}
        className="mt-1.5 h-2 overflow-hidden rounded-full bg-slate-100"
      >
        <div className="h-full rounded-full bg-brand transition-[width]" style={{ width: `${percent}%` }} />
      </div>
    </div>
  );
}

// ─── Trend chart ─────────────────────────────────────────────────────────────

import * as echartsCore from 'echarts/core';
import { type EChartsCoreOption } from 'echarts/core';
import { BarChart as EBarChart, HeatmapChart as EHeatmapChart, LineChart as ELineChart } from 'echarts/charts';
import {
  DataZoomComponent,
  GridComponent,
  LegendComponent,
  MarkAreaComponent,
  MarkLineComponent,
  TooltipComponent,
  VisualMapComponent,
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import EChartsReactCore from 'echarts-for-react/lib/core';

// Tree-shaken ECharts: only the pieces the trend chart draws.
echartsCore.use([
  EBarChart,
  ELineChart,
  EHeatmapChart,
  GridComponent,
  TooltipComponent,
  LegendComponent,
  DataZoomComponent,
  VisualMapComponent,
  MarkLineComponent,
  MarkAreaComponent,
  CanvasRenderer,
]);

export interface TrendPoint {
  /** Bucket key — the x identity (a date string, a month, …). */
  readonly key: string;
  /** Short axis label; falls back to the key. */
  readonly label?: string;
  readonly value: number;
  /** Extra lines for the hover tooltip ("12 converted"). */
  readonly details?: readonly string[];
}

/**
 * A vertical event marker on the time axis — "what changed here", drawn as a
 * dashed line at the bucket the event falls in with a small label at the top.
 * The same shape every analytics chart uses for the shared key-events timeline.
 */
export interface TrendMarker {
  /** The event's bucket key / date (YYYY-MM-DD or YYYY-MM); placed in the bucket it falls in. */
  readonly x: string;
  /** Short label shown at the top of the line. */
  readonly label: string;
  /** Line + label color; defaults to a neutral slate. */
  readonly color?: string;
}

/**
 * A shaded date range on the time axis — "the data here means something
 * different", e.g. a stretch when a tracking field was not being captured.
 * Covers every bucket from the one `from` falls in through the one `to` falls in.
 */
export interface TrendBand {
  /** First date in the range (YYYY-MM-DD or YYYY-MM). */
  readonly from: string;
  /** Last date in the range, inclusive. */
  readonly to: string;
  /** Short label shown at the top of the band. */
  readonly label: string;
}

/** A secondary line drawn over the series — a trailing average, a target. */
export interface TrendOverlay {
  readonly key: string;
  /** Legend label ("7-day average"). */
  readonly label: string;
  /** One value per point, in point order; null draws a gap. */
  readonly values: readonly (number | null)[];
  /** Overlay palette slot: 0 solid dark, 1 dashed muted, 2 dotted teal (a third line, e.g. a what-if), 3 dash-dot brown (a fourth). */
  readonly tone?: 0 | 1 | 2 | 3;
  /**
   * Draw in a SERIES_COLORS slot instead of the tone's color (the tone still
   * sets the stroke), so a line reads as belonging to the bars it describes.
   */
  readonly colorIndex?: number;
  /** Plot against the right-hand axis (see TrendChartProps.secondaryAxis), e.g. a rate over counts. */
  readonly axis?: typeof TREND_AXIS.secondary;
}

/** Which value axis an overlay is drawn against. */
export const TREND_AXIS = { secondary: 'secondary' } as const;

export type TrendChartVariant = 'bar' | 'line';

/** One split of the series when the chart is segmented (stacked bars / one line each). */
export interface TrendSeries {
  readonly key: string;
  readonly label: string;
  /** One value per point, in point order. */
  readonly values: readonly number[];
  /**
   * Bars sharing a stack id stack together; different ids sit side by side
   * in each bucket. Left out, every segment shares one stack.
   */
  readonly stack?: string;
  /** SERIES_COLORS slot to draw in; left out, the entry's position picks it. */
  readonly colorIndex?: number;
  /** Drawn pale: the remainder of a stack beside its solid part ("not signed" over "signed"). */
  readonly faded?: boolean;
}

/** The right-hand value axis, for overlays on a different scale than the bars. */
export interface TrendSecondaryAxis {
  /** Formats the axis ticks and those overlays' tooltip values ("25%"). */
  readonly formatValue: (value: number) => string;
  /** Fixed top of the axis (100 for a percentage); left out, it fits the data. */
  readonly max?: number;
}

/**
 * Categorical palette for segmented series, assigned in this fixed order —
 * never cycled. Validated (dataviz six checks, light surface): CVD-safe,
 * ≥3:1 on white. A 7th+ series must fold into "Other", which wears the
 * neutral SERIES_OTHER_COLOR outside the categorical set. The brand slot is
 * resolved from the CSS token at render (canvas cannot read CSS variables).
 */
export const SERIES_COLORS = [
  'var(--color-brand)',
  '#ea580c',
  '#0f9d8a',
  '#9333ea',
  '#4d7c0f',
  '#db2777',
] as const;
export const SERIES_OTHER_COLOR = '#94a3b8';
export const SERIES_OTHER_KEY = 'Other';

/** Portal tokens as literals — the canvas renderer cannot read CSS variables. */
const FALLBACK_BRAND = '#1851d1';
const INK = '#14213d';
const MUTED = '#667085';
const HAIRLINE = '#e3e9f1';

/** Opacity of a faded segment: the same hue, clearly secondary to the solid part. */
const FADED_OPACITY = 0.35;
const DEFAULT_STACK = 'total';

const OVERLAY_STROKES = [
  { color: INK, type: 'solid' as const },
  { color: MUTED, type: 'dashed' as const },
  { color: '#0f9d8a', type: 'dotted' as const },
  // Brown, outside SERIES_COLORS, so a fourth line never shares a swatch with a split segment.
  { color: '#854d0e', type: [8, 3, 2, 3] },
];

/** Below this plot width (px) a chart is on a phone: fewer axis ticks, smaller labels. */
export const NARROW_CHART_WIDTH = 480;

const LEGEND_FONT_PX = 11;
const LEGEND_ICON_PX = 10;
const LEGEND_ICON_GAP_PX = 5;
const LEGEND_ITEM_GAP_PX = 16;
/** One wrapped legend row: the 11px line plus ECharts' item gap, which it reuses between rows. */
const LEGEND_ROW_PX = 27;
/** Average glyph width at the legend's 11px, as a share of the font size. */
const GLYPH_WIDTH_RATIO = 0.5;
/** The grid's top under a one-row legend: the row plus clearance for the top axis label. */
const ONE_ROW_LEGEND_TOP_PX = 36;

/**
 * How many rows the native legend wraps to at a given chart width. ECharts
 * wraps a horizontal legend on its own but does not move the plot down, so
 * the grid's top has to make room. Text width is estimated (the canvas is not
 * measured), which is close enough for an 11px label set; on phone-width
 * charts only. Pure, for tests.
 */
export function legendRows(labels: readonly string[], width: number | undefined): number {
  // Wide charts keep the single-row layout they always had: the estimate is
  // only trusted where wrapping is near-certain and an overlap is likeliest.
  if (width === undefined || width >= NARROW_CHART_WIDTH || labels.length === 0) return 1;
  let rows = 1;
  let used = 0;
  for (const label of labels) {
    const item = LEGEND_ICON_PX + LEGEND_ICON_GAP_PX + label.length * LEGEND_FONT_PX * GLYPH_WIDTH_RATIO;
    const next = used === 0 ? item : used + LEGEND_ITEM_GAP_PX + item;
    if (next > width && used > 0) {
      rows += 1;
      used = item;
    } else {
      used = next;
    }
  }
  return rows;
}

/** The grid's top offset that clears a legend of these labels at this width. */
export function legendGridTop(labels: readonly string[], width: number | undefined): number {
  return ONE_ROW_LEGEND_TOP_PX + (legendRows(labels, width) - 1) * LEGEND_ROW_PX;
}

/**
 * The rendered width of a chart's container, tracked so the option can adapt
 * (legend rows, tick density) when a phone or a resized window narrows it.
 * Undefined until measured, and wherever ResizeObserver is missing (tests).
 */
function useContainerWidth(): [React.RefObject<HTMLDivElement | null>, number | undefined] {
  const ref = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState<number | undefined>(undefined);
  useEffect(() => {
    const element = ref.current;
    if (!element || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver((entries) => {
      const next = entries[0]?.contentRect.width;
      // Whole pixels only: sub-pixel jitter would rebuild the option for nothing.
      if (next !== undefined && next > 0) setWidth(Math.round(next));
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  return [ref, width];
}

/** At or under this many categories, a narrow chart slants its labels so every one shows. */
const SLANTED_LABEL_MAX_POINTS = 8;
const SLANTED_LABEL_DEGREES = 35;

/**
 * Axis label size, value-axis tick count and category-label slant for a
 * chart this wide. A long time series keeps flat labels and lets ECharts drop
 * the overlapping ones; a handful of named bars (cost categories) slants them
 * instead, since a dropped name there is a bar nobody can identify.
 */
function axisDensity(width: number | undefined, pointCount: number): { fontSize: number; splitNumber?: number; rotate?: number } {
  if (width === undefined || width >= NARROW_CHART_WIDTH) return { fontSize: 11 };
  return {
    fontSize: 10,
    splitNumber: 4,
    ...(pointCount <= SLANTED_LABEL_MAX_POINTS ? { rotate: SLANTED_LABEL_DEGREES } : {}),
  };
}

/** Default color for an event marker line + label (slate; distinct from the series). */
const MARKER_COLOR = '#475569';

/**
 * Maps each event marker to the axis category it belongs in — the last bucket
 * whose key is on or before the marker's date. Markers before the first bucket
 * are dropped (their bucket is off-screen). Keys are sortable date strings, so
 * a plain string compare places day, week and month buckets correctly.
 */
function markerLineData(
  markers: readonly TrendMarker[],
  points: readonly TrendPoint[],
): Array<{ xAxis: string; label: string; color: string }> {
  const lines: Array<{ xAxis: string; label: string; color: string }> = [];
  for (const marker of markers) {
    let index = -1;
    for (let i = 0; i < points.length; i += 1) {
      if (points[i]!.key <= marker.x) index = i;
      else break;
    }
    if (index < 0) continue;
    const point = points[index]!;
    lines.push({ xAxis: point.label ?? point.key, label: marker.label, color: marker.color || MARKER_COLOR });
  }
  return lines;
}

const BAND_FILL = 'rgba(148, 163, 184, 0.16)';

/**
 * Maps each band to the first and last axis categories it covers. A band that
 * starts before the first bucket is clipped to it; one that ends before the
 * first bucket, or starts after the last, is dropped.
 */
export function bandAreaData(
  bands: readonly TrendBand[],
  points: readonly TrendPoint[],
): Array<{ start: string; end: string; label: string }> {
  const areas: Array<{ start: string; end: string; label: string }> = [];
  const first = points[0];
  const last = points[points.length - 1];
  if (!first || !last) return areas;
  for (const band of bands) {
    if (band.to < first.key || band.from > last.key) continue;
    let startIndex = 0;
    let endIndex = 0;
    for (let i = 0; i < points.length; i += 1) {
      const key = points[i]!.key;
      if (key <= band.from) startIndex = i;
      if (key <= band.to) endIndex = i;
    }
    areas.push({
      start: points[startIndex]!.label ?? points[startIndex]!.key,
      end: points[endIndex]!.label ?? points[endIndex]!.key,
      label: band.label,
    });
  }
  return areas;
}

export interface TrendChartProps {
  readonly points: readonly TrendPoint[];
  /** Names the series for assistive tech and the legend. */
  readonly title: string;
  /** Unit word for the tooltip and the accessible table ("leads"). */
  readonly unit: string;
  /** Bars (default) or a line. */
  readonly variant?: TrendChartVariant;
  /**
   * Split the series: bars stack one segment per entry, the line variant
   * draws one line per entry. Colors follow entry order (see SERIES_COLORS);
   * an entry keyed "Other" is drawn neutral. `points` still carry the totals.
   */
  readonly series?: readonly TrendSeries[];
  /** Secondary lines over the series. */
  readonly overlays?: readonly TrendOverlay[];
  /**
   * Start with the main series (or its segments) switched off in the legend,
   * so the y-axis fits the overlays: a trend line under daily spikes reads
   * flat otherwise. The legend still switches them back on.
   */
  readonly hideSeries?: boolean;
  /** Vertical event markers on the time axis (the shared key-events timeline). */
  readonly markers?: readonly TrendMarker[];
  /** Shaded date ranges on the time axis, each with a label. */
  readonly bands?: readonly TrendBand[];
  /** A right-hand axis for the overlays marked `axis: 'secondary'`. */
  readonly secondaryAxis?: TrendSecondaryAxis;
  /** Pixel height of the plot area. */
  readonly height?: number;
  /**
   * Show every (n+1)th x-axis label: 0 labels every period. Left out, the
   * chart thins labels on its own to whatever fits.
   */
  readonly xAxisLabelInterval?: number;
  /**
   * Formats a value for the y-axis and tooltip — pass a compact currency
   * formatter when the series is dollars. Defaults to toLocaleString.
   */
  readonly formatValue?: (value: number) => string;
  readonly className?: string;
}

/** Color for the i-th segmented series, with the brand token resolved. */
function seriesColor(key: string, index: number, brand: string): string {
  if (key === SERIES_OTHER_KEY) return SERIES_OTHER_COLOR;
  const slot = SERIES_COLORS[index % SERIES_COLORS.length]!;
  return slot === 'var(--color-brand)' ? brand : slot;
}

const HTML_ESCAPES: Readonly<Record<string, string>> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

/**
 * Text made safe to place in tooltip HTML. ECharts renders a formatter's
 * string as HTML, and labels can come from outside (a search query, a traffic
 * source), so every caller-supplied string is escaped on the way in.
 */
function escapeHtml(text: string): string {
  return text.replace(/[&<>"']/g, (char) => HTML_ESCAPES[char] ?? char);
}

/**
 * One tooltip row: colored marker, label, formatted value. The marker is
 * ECharts' own HTML; the label and value are escaped.
 */
function tooltipRow(marker: string, label: string, value: string): string {
  return (
    `<div style="display:flex;justify-content:space-between;gap:16px;align-items:center">` +
    `<span>${marker}${escapeHtml(label)}</span><b style="font-variant-numeric:tabular-nums">${escapeHtml(value)}</b></div>`
  );
}

/**
 * Builds the ECharts option for a trend chart. Pure — exported so tests
 * assert the series structure and tooltip text without a canvas.
 *
 * @param props - The chart's props plus the resolved brand color.
 * @returns The option object handed to ECharts.
 */
export function buildTrendOption(
  props: Pick<
    TrendChartProps,
    'points' | 'unit' | 'variant' | 'series' | 'overlays' | 'hideSeries' | 'markers' | 'bands' | 'xAxisLabelInterval' | 'secondaryAxis'
  > & {
    formatValue: (value: number) => string;
    brand: string;
    /** The container's width in px, when measured: wraps the legend and thins the ticks. */
    width?: number;
  },
): EChartsCoreOption {
  const { points, unit, brand, formatValue } = props;
  const variant = props.variant ?? 'bar';
  const segments = props.series && props.series.length > 0 ? props.series : null;
  const overlays = props.overlays ?? [];
  const markerLines = markerLineData(props.markers ?? [], points);
  const bandAreas = bandAreaData(props.bands ?? [], points);
  // A line through one point draws nothing; a lone point (one day, say) shows its dot.
  const showSymbol = points.length === 1;

  const mainSeries =
    segments === null
      ? [
          {
            name: unit,
            type: variant,
            data: points.map((point) => point.value),
            itemStyle: { color: brand, borderRadius: variant === 'bar' ? [4, 4, 0, 0] : 0 },
            lineStyle: { color: brand, width: 2 },
            symbol: 'circle',
            symbolSize: 6,
            showSymbol,
            emphasis: { focus: 'series' },
            barMaxWidth: 28,
          },
        ]
      : segments.map((entry, index) => ({
          name: entry.label,
          type: variant,
          ...(variant === 'bar' ? { stack: entry.stack ?? DEFAULT_STACK } : {}),
          data: [...entry.values],
          itemStyle: {
            color: seriesColor(entry.key, entry.colorIndex ?? index, brand),
            ...(entry.faded ? { opacity: FADED_OPACITY } : {}),
            ...(variant === 'bar' ? { borderRadius: 0 } : {}),
          },
          lineStyle: { color: seriesColor(entry.key, entry.colorIndex ?? index, brand), width: 2 },
          symbol: 'circle',
          symbolSize: 6,
          showSymbol,
          emphasis: { focus: 'series' },
          barMaxWidth: 28,
        }));

  // Event markers ride on the first series' markLine (drawn once, spanning the
  // full plot height). Kept off the tooltip and legend so they never toggle.
  if (markerLines.length > 0 && mainSeries[0]) {
    (mainSeries[0] as Record<string, unknown>)['markLine'] = {
      silent: true,
      symbol: 'none',
      data: markerLines.map((line) => ({
        xAxis: line.xAxis,
        lineStyle: { color: line.color, type: 'dashed', width: 1.5 },
        label: {
          show: true,
          position: 'end',
          formatter: line.label,
          color: line.color,
          fontSize: 10,
          fontWeight: 'bold',
        },
      })),
    };
  }

  // Bands ride on the first series' markArea, behind the bars, like markers.
  if (bandAreas.length > 0 && mainSeries[0]) {
    (mainSeries[0] as Record<string, unknown>)['markArea'] = {
      silent: true,
      itemStyle: { color: BAND_FILL },
      label: { show: true, position: 'insideTop', color: MUTED, fontSize: 10, fontWeight: 'bold' },
      data: bandAreas.map((area) => [{ xAxis: area.start, name: area.label }, { xAxis: area.end }]),
    };
  }

  const secondaryAxis = props.secondaryAxis;
  const onSecondary = (overlay: TrendOverlay): boolean => secondaryAxis !== undefined && overlay.axis === TREND_AXIS.secondary;
  // Tooltip rows for right-axis lines take that axis's format ("25%", not "25").
  const secondaryNames = new Set(overlays.filter(onSecondary).map((overlay) => overlay.label));
  const overlaySeries = overlays.map((overlay) => {
    const stroke = OVERLAY_STROKES[overlay.tone ?? 0]!;
    const color = overlay.colorIndex === undefined ? stroke.color : seriesColor('', overlay.colorIndex, brand);
    return {
      name: overlay.label,
      type: 'line' as const,
      ...(onSecondary(overlay) ? { yAxisIndex: 1 } : {}),
      data: [...overlay.values],
      lineStyle: { color, width: 2, type: stroke.type },
      itemStyle: { color },
      symbol: 'none',
      z: 5,
      // Averages smooth a jagged series; keep them visually calm.
      smooth: 0.25,
    };
  });

  const density = axisDensity(props.width, points.length);
  const legendLabels = [...mainSeries.map((entry) => entry.name), ...overlaySeries.map((entry) => entry.name)];
  const valueAxis = {
    type: 'value',
    ...(density.splitNumber !== undefined ? { splitNumber: density.splitNumber } : {}),
    splitLine: { lineStyle: { color: HAIRLINE } },
    axisLabel: {
      color: MUTED,
      fontSize: density.fontSize,
      formatter: (value: number) => formatValue(value),
    },
  };

  return {
    animationDuration: 250,
    grid: { left: 8, right: 8, top: legendGridTop(legendLabels, props.width), bottom: 44, containLabel: true },
    legend: {
      // Native legend: clicking an entry toggles its series, which covers
      // segment and overlay toggling without custom state.
      ...(props.hideSeries ? { selected: Object.fromEntries(mainSeries.map((entry) => [entry.name, false])) } : {}),
      top: 0,
      left: 0,
      icon: 'roundRect',
      itemWidth: 10,
      itemHeight: 10,
      itemGap: 16,
      textStyle: { color: MUTED, fontSize: 11 },
    },
    dataZoom: [{ type: 'inside', zoomOnMouseWheel: 'ctrl', moveOnMouseWheel: false }],
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: variant === 'bar' ? 'shadow' : 'line' },
      backgroundColor: '#ffffff',
      borderColor: HAIRLINE,
      textStyle: { color: INK, fontSize: 12 },
      extraCssText: 'box-shadow: 0 3px 12px rgba(20,45,80,0.12); border-radius: 9px;',
      formatter: (params: unknown) => {
        const items = (Array.isArray(params) ? params : [params]) as Array<{
          seriesName: string;
          dataIndex: number;
          value: number | null;
          marker: string;
        }>;
        const first = items[0];
        if (!first) return '';
        const point = points[first.dataIndex];
        if (!point) return '';
        const lines = [`<div style="font-weight:700;margin-bottom:4px">${escapeHtml(point.label ?? point.key)}</div>`];
        for (const item of items) {
          // null and NaN are gaps (a period with no value), never "$0".
          if (item.value === null || item.value === undefined || Number.isNaN(item.value)) continue;
          const format = secondaryAxis && secondaryNames.has(item.seriesName) ? secondaryAxis.formatValue : formatValue;
          lines.push(tooltipRow(item.marker, item.seriesName, format(item.value)));
        }
        for (const detail of point.details ?? []) {
          lines.push(`<div style="color:${MUTED};margin-top:2px">${escapeHtml(detail)}</div>`);
        }
        return lines.join('');
      },
    },
    xAxis: {
      type: 'category',
      data: points.map((point) => point.label ?? point.key),
      axisLine: { lineStyle: { color: HAIRLINE } },
      axisTick: { show: false },
      axisLabel: {
        color: MUTED,
        fontSize: density.fontSize,
        hideOverlap: props.xAxisLabelInterval === undefined,
        ...(props.xAxisLabelInterval !== undefined ? { interval: props.xAxisLabelInterval } : {}),
        ...(density.rotate !== undefined ? { rotate: density.rotate } : {}),
      },
    },
    // An array only with a right-hand axis, so single-axis charts keep their shape.
    yAxis: secondaryAxis
      ? [
          valueAxis,
          {
            type: 'value',
            min: 0,
            ...(secondaryAxis.max !== undefined ? { max: secondaryAxis.max } : {}),
            ...(density.splitNumber !== undefined ? { splitNumber: density.splitNumber } : {}),
            // One set of gridlines: the left axis owns them.
            splitLine: { show: false },
            axisLabel: {
              color: MUTED,
              fontSize: density.fontSize,
              formatter: (value: number) => secondaryAxis.formatValue(value),
            },
          },
        ]
      : valueAxis,
    series: [...mainSeries, ...overlaySeries],
  };
}

/**
 * One series over time — leads per day, bookings per week — as bars or a
 * line, with optional stacked segments, overlay lines and a native legend
 * whose entries toggle their series. ECharts underneath (crosshair tooltip,
 * ctrl+wheel zoom on long ranges); the full data is also rendered as a
 * visually hidden table, so a screen reader gets the numbers, not a canvas.
 *
 * @param props - The points, the series name, variant, splits and overlays.
 * @returns The chart.
 */
/**
 * The brand color as a literal: the canvas cannot read CSS variables, so the
 * token resolves once per chart (portal themes swap it per app); tests and
 * SSR fall back to BCE blue.
 */
function useBrandColor(): string {
  return useMemo(() => {
    if (typeof window === 'undefined') return FALLBACK_BRAND;
    const resolved = getComputedStyle(document.documentElement)
      .getPropertyValue('--color-brand')
      .trim();
    return resolved === '' ? FALLBACK_BRAND : resolved;
  }, []);
}

export function TrendChart({
  points,
  title,
  unit,
  variant = 'bar',
  series,
  overlays = [],
  hideSeries = false,
  markers = [],
  bands = [],
  secondaryAxis,
  height = 260,
  xAxisLabelInterval,
  formatValue = (value: number) => value.toLocaleString(),
  className,
}: TrendChartProps): React.ReactElement {
  const tableId = useId();
  const brand = useBrandColor();
  const [containerRef, width] = useContainerWidth();
  const option = useMemo(
    () =>
      buildTrendOption({
        points,
        unit,
        variant,
        ...(series !== undefined ? { series } : {}),
        overlays,
        hideSeries,
        markers,
        bands,
        ...(secondaryAxis !== undefined ? { secondaryAxis } : {}),
        formatValue,
        brand,
        ...(width !== undefined ? { width } : {}),
        ...(xAxisLabelInterval !== undefined ? { xAxisLabelInterval } : {}),
      }),
    [points, unit, variant, series, overlays, hideSeries, markers, bands, secondaryAxis, formatValue, brand, width, xAxisLabelInterval],
  );
  const segments = series && series.length > 0 ? series : null;
  // Extra legend rows on a narrow screen add height rather than squeeze the plot.
  const legendLabels = [...(segments ? segments.map((entry) => entry.label) : [unit]), ...overlays.map((overlay) => overlay.label)];
  const plotHeight = height + (legendRows(legendLabels, width) - 1) * LEGEND_ROW_PX;

  return (
    <div className={className}>
      <div ref={containerRef} role="img" aria-label={title} aria-describedby={tableId}>
        <EChartsReactCore
          // The full core namespace: the wrapper calls getInstanceByDom and
          // friends, not just init.
          echarts={echartsCore as never}
          option={option}
          notMerge
          style={{ height: plotHeight, width: '100%' }}
        />
      </div>
      {/* The wrapper does the clipping: a table ignores overflow, so sr-only on
          the table itself still widens the page by its full content width. */}
      <div className="sr-only">
        <table id={tableId}>
          <caption>
            {title}
            {bands.map((band) => ` (${band.label}: ${band.from} to ${band.to})`).join('')}
          </caption>
          <thead>
            <tr>
              <th>Period</th>
              <th>{unit}</th>
              {segments?.map((entry) => (
                <th key={entry.key}>{entry.label}</th>
              ))}
              {overlays.map((overlay) => (
                <th key={overlay.key}>{overlay.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {points.map((point, index) => (
              <tr key={point.key}>
                <td>{point.label ?? point.key}</td>
                <td>{Number.isNaN(point.value) ? '' : point.value}</td>
                {segments?.map((entry) => {
                  const value = entry.values[index] ?? 0;
                  return <td key={entry.key}>{Number.isNaN(value) ? '' : value}</td>;
                })}
                {overlays.map((overlay) => (
                  <td key={overlay.key}>{overlay.values[index] ?? ''}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Bars-only alias kept for callers that predate the variant prop. */
export const BarTrendChart = TrendChart;

// ─── Band chart: a distribution per category with an actual overlaid ──────

/** Accent for the "actual" bars — the brand yellow, as a literal for the canvas. */
const ACCENT = '#ffc72c';

export interface BandPoint {
  readonly key: string;
  readonly label?: string;
  /** Bottom and top of the shaded band (e.g. the 25th and 90th percentile). */
  readonly low: number;
  readonly high: number;
  /** The central line (mean or median). */
  readonly center: number;
  /** Today's value for this category, drawn as a bar; null draws nothing. */
  readonly actual?: number | null;
  /** Extra tooltip lines. */
  readonly details?: readonly string[];
}

export interface BandChartProps {
  readonly points: readonly BandPoint[];
  readonly title: string;
  readonly unit: string;
  readonly bandLabel: string;
  readonly centerLabel: string;
  readonly actualLabel: string;
  readonly height?: number;
  readonly className?: string;
}

/**
 * Builds the option for a band chart: a shaded low–high band, a center line
 * and optional actual bars per category. Pure, for tests.
 */
export function buildBandOption(
  props: Pick<BandChartProps, 'points' | 'unit' | 'bandLabel' | 'centerLabel' | 'actualLabel'> & { brand: string; width?: number },
): EChartsCoreOption {
  const { points, brand } = props;
  const hasActual = points.some((point) => point.actual !== null && point.actual !== undefined);
  const legendLabels = [props.bandLabel, props.centerLabel, ...(hasActual ? [props.actualLabel] : [])];
  return {
    animationDuration: 250,
    grid: { left: 8, right: 8, top: legendGridTop(legendLabels, props.width), bottom: 32, containLabel: true },
    legend: { top: 0, left: 0, icon: 'roundRect', itemWidth: 10, itemHeight: 10, itemGap: 16, textStyle: { color: MUTED, fontSize: 11 }, data: [props.bandLabel, props.centerLabel, ...(hasActual ? [props.actualLabel] : [])] },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      backgroundColor: '#ffffff',
      borderColor: HAIRLINE,
      textStyle: { color: INK, fontSize: 12 },
      extraCssText: 'box-shadow: 0 3px 12px rgba(20,45,80,0.12); border-radius: 9px;',
      formatter: (params: unknown) => {
        const items = (Array.isArray(params) ? params : [params]) as Array<{ dataIndex: number }>;
        const point = items[0] ? points[items[0].dataIndex] : undefined;
        if (!point) return '';
        const lines = [`<div style="font-weight:700;margin-bottom:4px">${escapeHtml(point.label ?? point.key)}</div>`];
        if (point.actual !== null && point.actual !== undefined) lines.push(tooltipRow('', props.actualLabel, point.actual.toLocaleString()));
        lines.push(tooltipRow('', props.centerLabel, point.center.toLocaleString()));
        lines.push(tooltipRow('', props.bandLabel, `${point.low.toLocaleString()} – ${point.high.toLocaleString()}`));
        for (const detail of point.details ?? []) lines.push(`<div style="color:${MUTED};margin-top:2px">${escapeHtml(detail)}</div>`);
        return lines.join('');
      },
    },
    xAxis: {
      type: 'category',
      data: points.map((point) => point.label ?? point.key),
      axisLine: { lineStyle: { color: HAIRLINE } },
      axisTick: { show: false },
      axisLabel: { color: MUTED, fontSize: 11, hideOverlap: true },
    },
    yAxis: {
      type: 'value',
      minInterval: 1,
      splitLine: { lineStyle: { color: HAIRLINE } },
      axisLabel: { color: MUTED, fontSize: 11 },
    },
    series: [
      // The band is two stacked areas: an invisible floor up to `low`, then a
      // shaded slab of height high − low on top of it.
      { name: props.bandLabel, type: 'line', stack: 'band', data: points.map((point) => point.low), lineStyle: { opacity: 0 }, itemStyle: { color: brand, opacity: 0 }, areaStyle: { opacity: 0 }, symbol: 'none', silent: true, tooltip: { show: false }, legendHoverLink: false, z: 1 },
      { name: props.bandLabel, type: 'line', stack: 'band', data: points.map((point) => Math.max(0, point.high - point.low)), lineStyle: { opacity: 0 }, itemStyle: { color: brand, opacity: 0.16 }, areaStyle: { color: brand, opacity: 0.16 }, symbol: 'none', silent: true, z: 1 },
      { name: props.centerLabel, type: 'line', data: points.map((point) => point.center), lineStyle: { color: brand, width: 2.5 }, itemStyle: { color: brand }, symbol: 'circle', symbolSize: 7, z: 3 },
      ...(hasActual
        ? [{ name: props.actualLabel, type: 'bar', data: points.map((point) => point.actual ?? null), itemStyle: { color: ACCENT, borderRadius: [4, 4, 0, 0] }, barMaxWidth: 22, z: 2 }]
        : []),
    ],
  };
}

/**
 * A distribution per category with an actual on top: what a normal hour or
 * day delivers (shaded 25th–90th percentile band plus a mean line) against
 * what happened today (bars). Hidden table for assistive tech, as TrendChart.
 */
export function BandChart({ points, title, unit, bandLabel, centerLabel, actualLabel, height = 260, className }: BandChartProps): React.ReactElement {
  const tableId = useId();
  const brand = useBrandColor();
  const [containerRef, width] = useContainerWidth();
  const option = useMemo(
    () => buildBandOption({ points, unit, bandLabel, centerLabel, actualLabel, brand, ...(width !== undefined ? { width } : {}) }),
    [points, unit, bandLabel, centerLabel, actualLabel, brand, width],
  );
  return (
    <div className={className}>
      <div ref={containerRef} role="img" aria-label={title} aria-describedby={tableId}>
        <EChartsReactCore echarts={echartsCore as never} option={option} notMerge style={{ height, width: '100%' }} />
      </div>
      <div className="sr-only">
        <table id={tableId}>
          <caption>{title}</caption>
          <thead>
            <tr>
              <th>Category</th>
              <th>{centerLabel}</th>
              <th>{bandLabel}</th>
              <th>{actualLabel}</th>
            </tr>
          </thead>
          <tbody>
            {points.map((point) => (
              <tr key={point.key}>
                <td>{point.label ?? point.key}</td>
                <td>{point.center}</td>
                <td>{`${point.low} – ${point.high}`}</td>
                <td>{point.actual ?? ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Heatmap: rows × columns of values ────────────────────────────────────

export interface HeatmapChartProps {
  /** Row labels, top to bottom. */
  readonly rows: readonly string[];
  /** Column labels, left to right. */
  readonly columns: readonly string[];
  /** values[rowIndex][columnIndex]; null = no data (drawn blank, read "no data"), a missing cell reads zero. */
  readonly values: readonly (readonly (number | null)[])[];
  readonly title: string;
  readonly unit: string;
  readonly height?: number;
  /** Formats a value for the cells, tooltip, legend and table ($, %). Defaults to toLocaleString. */
  readonly formatValue?: (value: number) => string;
  /** Print each value in its cell (default). Turn off for wide grids where the labels would crowd. */
  readonly showValues?: boolean;
  /** Draw the color scale under the grid, low to high. */
  readonly legend?: boolean;
  readonly className?: string;
}

/** Lightest step of the single-hue ramp the heatmap runs from (portal mist tint). */
const HEAT_LOW = '#eef3fc';
const HEAT_LEGEND_PX = 40;
const NO_DATA = 'No data';

const defaultFormat = (value: number): string => value.toLocaleString();

/** The cell's value, null for no data. A cell past the end of its row reads zero. */
function heatValue(values: HeatmapChartProps['values'], row: number, column: number): number | null {
  const value = values[row]?.[column];
  return value === undefined ? 0 : value;
}

/** Builds the heatmap option. Pure, for tests. */
export function buildHeatmapOption(
  props: Pick<HeatmapChartProps, 'rows' | 'columns' | 'values' | 'unit' | 'formatValue' | 'showValues' | 'legend'> & { brand: string },
): EChartsCoreOption {
  const { rows, columns, values, unit, brand } = props;
  const format = props.formatValue ?? defaultFormat;
  const showValues = props.showValues ?? true;
  const data: Array<[number, number, number | '-']> = [];
  let max = 0;
  rows.forEach((_, rowIndex) => {
    columns.forEach((__, columnIndex) => {
      const value = heatValue(values, rowIndex, columnIndex);
      if (value !== null) max = Math.max(max, value);
      // ECharts draws '-' as an empty cell, outside the visual map.
      data.push([columnIndex, rowIndex, value ?? '-']);
    });
  });
  const top = Math.max(1, max);
  return {
    animationDuration: 250,
    grid: { left: 8, right: 8, top: 8, bottom: props.legend ? 32 + HEAT_LEGEND_PX : 32, containLabel: true },
    tooltip: {
      backgroundColor: '#ffffff',
      borderColor: HAIRLINE,
      textStyle: { color: INK, fontSize: 12 },
      extraCssText: 'box-shadow: 0 3px 12px rgba(20,45,80,0.12); border-radius: 9px;',
      formatter: (params: unknown) => {
        const item = params as { value: [number, number, number | '-'] };
        const [column, row, value] = item.value;
        return `<div style="font-weight:700;margin-bottom:4px">${escapeHtml(rows[row] ?? '')} · ${escapeHtml(columns[column] ?? '')}</div>${tooltipRow('', unit, value === '-' ? NO_DATA : format(value))}`;
      },
    },
    xAxis: { type: 'category', data: [...columns], axisLine: { show: false }, axisTick: { show: false }, axisLabel: { color: MUTED, fontSize: 11 }, splitArea: { show: false } },
    yAxis: { type: 'category', data: [...rows], inverse: true, axisLine: { show: false }, axisTick: { show: false }, axisLabel: { color: INK, fontSize: 12 } },
    visualMap: {
      show: props.legend ?? false,
      type: 'continuous',
      min: 0,
      max: top,
      orient: 'horizontal',
      left: 'center',
      bottom: 0,
      itemWidth: 12,
      itemHeight: 160,
      calculable: false,
      text: [format(top), format(0)],
      // The hover indicator on the scale reads in the same units as the cells.
      formatter: (value: number) => format(value),
      textStyle: { color: MUTED, fontSize: 11 },
      inRange: { color: [HEAT_LOW, brand] },
    },
    series: [
      {
        type: 'heatmap',
        data,
        // Draw every cell at once. Progressive (chunked, per animation frame)
        // rendering left a 157 × 157 cohort grid half drawn in a background
        // tab, and a few thousand rectangles are cheap in one pass anyway.
        progressive: 0,
        itemStyle: { borderColor: '#ffffff', borderWidth: 2, borderRadius: 4 },
        label: {
          show: showValues,
          color: INK,
          fontSize: 11,
          formatter: (item: { value: [number, number, number | '-'] }) => (item.value[2] === '-' || item.value[2] === 0 ? '' : format(item.value[2])),
        },
        emphasis: { itemStyle: { borderColor: INK, borderWidth: 1 } },
      },
    ],
  };
}

/** What a screen reader hears for one cell: "Mon, 9am: 42 clicks". */
export function heatmapCellLabel(row: string, column: string, value: number | null, unit: string, format: (value: number) => string = defaultFormat): string {
  return `${row}, ${column}: ${value === null ? NO_DATA.toLowerCase() : `${format(value)} ${unit}`}`;
}

/**
 * A grid of values (weekdays × hours, say) on a single-hue ramp from the
 * portal mist tint to the brand color, optionally with the values printed in
 * the cells and the scale drawn underneath. Cells with no data stay blank.
 * Hidden table for assistive tech, as TrendChart, each cell labeled with its
 * row, column and value.
 */
export function HeatmapChart({
  rows,
  columns,
  values,
  title,
  unit,
  height = 240,
  formatValue,
  showValues = true,
  legend = false,
  className,
}: HeatmapChartProps): React.ReactElement {
  const tableId = useId();
  const brand = useBrandColor();
  const format = formatValue ?? defaultFormat;
  const option = useMemo(
    () => buildHeatmapOption({ rows, columns, values, unit, brand, formatValue: format, showValues, legend }),
    [rows, columns, values, unit, brand, format, showValues, legend],
  );
  return (
    <div className={className}>
      <div role="img" aria-label={title} aria-describedby={tableId}>
        <EChartsReactCore echarts={echartsCore as never} option={option} notMerge style={{ height, width: '100%' }} />
      </div>
      <div className="sr-only">
        <table id={tableId}>
          <caption>{title}</caption>
          <thead>
            <tr>
              <th></th>
              {columns.map((column) => (
                <th key={column}>{column}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => (
              <tr key={row}>
                <th scope="row">{row}</th>
                {columns.map((column, columnIndex) => {
                  const value = heatValue(values, rowIndex, columnIndex);
                  return (
                    <td key={column} aria-label={heatmapCellLabel(row, column, value, unit, format)}>
                      {value === null ? NO_DATA : format(value)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Curve chart ─────────────────────────────────────────────────────────────

/** How a curve's line is drawn. */
export const CURVE_STROKE = { solid: 'solid', dashed: 'dashed', dotted: 'dotted' } as const;
export type CurveStroke = (typeof CURVE_STROKE)[keyof typeof CURVE_STROKE];

/** One line on a curve chart, colored by the caller (a scale or a categorical slot). */
export interface Curve {
  readonly key: string;
  readonly label: string;
  /** One value per category; null draws a gap. */
  readonly values: readonly (number | null)[];
  /** A literal color: the canvas cannot read CSS variables. */
  readonly color: string;
  readonly stroke?: CurveStroke;
  /** 0 to 1; older years in a scale fade back. */
  readonly opacity?: number;
}

export interface CurveChartProps {
  /** The x categories, in order ("10", "20", …). */
  readonly categories: readonly string[];
  /** Word for the x axis in the tooltip header and accessible table ("people"). */
  readonly categoryUnit: string;
  readonly curves: readonly Curve[];
  /** Names the chart for assistive tech. */
  readonly title: string;
  readonly height?: number;
  /** Small multiples: fewer ticks, no grid labels on x but the ends. */
  readonly compact?: boolean;
  /** A key above the plot naming each curve (display only; the caller owns toggling). */
  readonly showLegend?: boolean;
  readonly formatValue?: (value: number) => string;
  readonly className?: string;
}

/**
 * Builds the ECharts option for a curve chart. Pure, exported for tests. The
 * tooltip lists every curve at the hovered category, highest value first, so
 * it reads top to bottom like the lines.
 *
 * @param props - Categories, curves, formatter, measured width.
 * @returns The option object handed to ECharts.
 */
export function buildCurveOption(
  props: Pick<CurveChartProps, 'categories' | 'categoryUnit' | 'curves' | 'compact' | 'showLegend'> & {
    formatValue: (value: number) => string;
    width?: number;
  },
): EChartsCoreOption {
  const { categories, categoryUnit, curves, formatValue } = props;
  const density = axisDensity(props.width, categories.length);
  const top = props.showLegend ? legendGridTop(curves.map((curve) => curve.label), props.width) : 12;
  return {
    animationDuration: 250,
    grid: { left: 8, right: 12, top, bottom: props.compact ? 8 : 28, containLabel: true },
    ...(props.showLegend
      ? {
          legend: {
            top: 0,
            left: 0,
            selectedMode: false,
            itemWidth: 18,
            itemHeight: 8,
            itemGap: 16,
            textStyle: { color: MUTED, fontSize: 11 },
          },
        }
      : {}),
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'line' },
      backgroundColor: '#ffffff',
      borderColor: HAIRLINE,
      textStyle: { color: INK, fontSize: 12 },
      extraCssText: 'box-shadow: 0 3px 12px rgba(20,45,80,0.12); border-radius: 9px;',
      formatter: (params: unknown) => {
        const items = (Array.isArray(params) ? params : [params]) as Array<{
          seriesName: string;
          dataIndex: number;
          value: number | null;
          marker: string;
        }>;
        const first = items[0];
        if (!first) return '';
        const lines = [
          `<div style="font-weight:700;margin-bottom:4px">${escapeHtml(`${categories[first.dataIndex] ?? ''} ${categoryUnit}`)}</div>`,
        ];
        const ranked = items
          .filter((item) => item.value !== null && item.value !== undefined && !Number.isNaN(item.value))
          .sort((a, b) => (b.value ?? 0) - (a.value ?? 0));
        for (const item of ranked) lines.push(tooltipRow(item.marker, item.seriesName, formatValue(item.value ?? 0)));
        return lines.join('');
      },
    },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: [...categories],
      axisLine: { lineStyle: { color: HAIRLINE } },
      axisTick: { show: false },
      axisLabel: {
        color: MUTED,
        fontSize: density.fontSize,
        hideOverlap: true,
        ...(props.compact ? { interval: (index: number) => index === 0 || index === categories.length - 1 } : {}),
      },
    },
    yAxis: {
      type: 'value',
      ...(props.compact ? { splitNumber: 3 } : density.splitNumber !== undefined ? { splitNumber: density.splitNumber } : {}),
      splitLine: { lineStyle: { color: HAIRLINE } },
      axisLabel: { color: MUTED, fontSize: density.fontSize, formatter: (value: number) => formatValue(value) },
    },
    series: curves.map((curve) => ({
      name: curve.label,
      type: 'line' as const,
      data: [...curve.values],
      connectNulls: false,
      symbol: 'none',
      lineStyle: {
        color: curve.color,
        width: 2,
        type: curve.stroke ?? CURVE_STROKE.solid,
        opacity: curve.opacity ?? 1,
      },
      itemStyle: { color: curve.color, opacity: curve.opacity ?? 1 },
      emphasis: { focus: 'series' },
    })),
  };
}

/**
 * Several lines over one category axis, each with its own color, stroke and
 * opacity: price curves by headcount, cost lines beside them. Unlike
 * TrendChart, the caller assigns colors (a sequential scale for years, a
 * categorical slot for the rest) and draws its own legend, so lines can be
 * toggled from page controls. A visually hidden table carries the numbers.
 *
 * @param props - Categories, curves and display options.
 * @returns The chart.
 */
export function CurveChart({
  categories,
  categoryUnit,
  curves,
  title,
  height = 280,
  compact = false,
  showLegend = false,
  formatValue = (value: number) => value.toLocaleString(),
  className,
}: CurveChartProps): React.ReactElement {
  const tableId = useId();
  const [containerRef, width] = useContainerWidth();
  const option = useMemo(
    () =>
      buildCurveOption({
        categories,
        categoryUnit,
        curves,
        compact,
        showLegend,
        formatValue,
        ...(width !== undefined ? { width } : {}),
      }),
    [categories, categoryUnit, curves, compact, showLegend, formatValue, width],
  );
  const plotHeight = showLegend ? height + (legendRows(curves.map((curve) => curve.label), width) - 1) * LEGEND_ROW_PX : height;
  return (
    <div className={className}>
      <div ref={containerRef} role="img" aria-label={title} aria-describedby={tableId}>
        <EChartsReactCore echarts={echartsCore as never} option={option} notMerge style={{ height: plotHeight, width: '100%' }} />
      </div>
      <div className="sr-only">
        <table id={tableId}>
          <caption>{title}</caption>
          <thead>
            <tr>
              <th>{categoryUnit}</th>
              {curves.map((curve) => (
                <th key={curve.key}>{curve.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {categories.map((category, index) => (
              <tr key={category}>
                <td>{category}</td>
                {curves.map((curve) => {
                  const value = curve.values[index];
                  return <td key={curve.key}>{value === null || value === undefined ? '' : formatValue(value)}</td>;
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
